import { path } from 'ramda';
import { createSelector } from 'reselect';

import { RootState } from 'state/reducer';

import { CardSequenceViewInterface, LauncherType } from 'types';
import { getMappedViewCards } from 'utility';
import {
  DealerDotComSiteIntegrationInterface,
  GenericSiteIntegrationInterface,
  SiteIntegrationType
} from '../../types/SiteIntegrationInterface';

export const themeSelector = createSelector(
  (state: RootState) => state.ui.theme,
  theme => theme
);

export const webMoveUpSelector = createSelector(
  (state: RootState) => state.ui.webMoveUp,
  webMoveUp => webMoveUp
);

export const mobileMoveUpSelector = createSelector(
  (state: RootState) => state.ui.mobileMoveUp,
  mobileMoveUp => mobileMoveUp
);

export const navButtonsSelector = createSelector(
  (state: RootState) => state.ui.navButtons,
  navButtons => navButtons
);

export const navSelector = createSelector(
  (state: RootState) => state.ui.nav,
  (state: RootState) => state.ui.launcher.type,
  navButtonsSelector,
  (nav, launcherType, navButtons) => (nav && !!navButtons.length) || launcherType === LauncherType.BAR
);

export const launcherSelector = createSelector(
  (state: RootState) => state.ui.launcher,
  launcher => launcher
);

export const launcherCardsSelector = createSelector(
  (state: RootState) => state.ui.launcher.cardIds,
  (state: RootState) => state.entities.cards,
  (launcherCardIds, cards) => {
    return getMappedViewCards(launcherCardIds, cards);
  }
);

export const windowSelector = createSelector(
  (state: RootState) => state.ui.window,
  window => window
);

export const toastSelector = createSelector(
  (state: RootState) => state.ui.toast,
  toast => toast
);

export const progressSelector = createSelector(
  (state: RootState, viewId: string) => state.entities.views.byId[viewId],
  (cardSequence: CardSequenceViewInterface) => (cardSequence ? cardSequence.progress : 0)
);

// @ts-ignore
export const toolbarModeSelector = createSelector(
  (state: RootState) => state.ui.toolbarMode,
  toolbarMode => toolbarMode
);

export const isToolbarEnabledSelector = createSelector(
  (state: RootState) => state.ui.behavior.toolbarMode,
  toolbarMode => toolbarMode
);

export const behaviorSelector = createSelector(
  (state: RootState) => state.ui.behavior,
  behavior => behavior
);

export const isTranslatorDisabledSelector = createSelector(
  (state: RootState) => state.ui.behavior.disableTranslator,
  disableTranslator => disableTranslator
);

export const homeViewIdSelector = createSelector(
  (state: RootState) => state.ui.homeViewId,
  homeViewId => homeViewId
);

export const defaultViewIdSelector = createSelector(homeViewIdSelector, isToolbarEnabledSelector, navButtonsSelector, (homeViewId, isToolbarEnabled, navButtons) => (isToolbarEnabled ? path([0, 'viewId'], navButtons) : homeViewId));

// Conversation
export const playSoundSelector = createSelector(
  (state: RootState) => state.ui.behavior.newMessage.sound,
  (state: RootState) => state.ui.soundIsPlaying,
  (soundEnabled, soundIsPlaying) => soundEnabled && soundIsPlaying
);

export const tabFlashSelector = createSelector(
  (state: RootState) => state.ui.behavior.newMessage.tabFlash,
  tabFlash => tabFlash
);

export const titleSelector = createSelector(
  (state: RootState) => state.ui.title,
  title => title
);

export const exitTriggerAllowedSelector = createSelector(
  (state: RootState) => state.ui.isExitTriggerAllowed,
  isExitTriggerAllowed => isExitTriggerAllowed
);

export const isWidgetOpen = createSelector(
  (state: RootState) => state.ui.window,
  (state: RootState) => state.ui.launcher.open,
  (isWindowOpen, isLauncherOpen) => isWindowOpen || isLauncherOpen
);

export const isLauncherOpenSelector = (state: RootState) => state.ui.launcher.open;

export const isWideSelector = createSelector(
  (state: RootState) => state.ui.window,
  (state: RootState) => state.ui.launcher,
  (state: RootState) => state.ui.nav,
  (window, launcher, nav) => window || launcher.type === LauncherType.BAR || nav || (launcher.open && !!launcher.cardIds.length)
);

export const initialCardIdsSelector = (state: RootState) => state.ui.launcher.initialCardIds || [];

// *********************** PREVIEW MODE SELECTORS ***********************************

export const rpPreviewModeSelector = createSelector(
  (state: RootState) => state.ui.rppreview,
  rppreview => rppreview
);

export const wipViewIdSelector = createSelector(
  (state: RootState) => state.ui.wipViewId,
  wipViewId => wipViewId
);

export const wipWebdeskSelector = createSelector(
  (state: RootState) => state.ui.homeViewId,
  (state: RootState) => state.ui.navButtons,
  (state: RootState) => state.ui.behavior,
  (state: RootState) => state.ui.launcher.type,
  (state: RootState) => state.ui.launcher.cardIds,
  (state: RootState) => state.ui.theme.colors,
  (state: RootState) => state.ui.theme.navColors,
  (state: RootState) => state.ui.appearance.position,
  (homeViewId, navButtons, behavior, launcherType, launcherCardIds, themeColors, navColors, position) => ({
    navigation: {
      homeViewId,
      navButtons
    },
    behavior,
    launcher: {
      desktop: launcherType,
      cardIds: launcherCardIds
    },
    appearance: {
      color: themeColors.original.split('#')[1],
      navColor: navColors.original.split('#')[1],
      position
    }
  })
);

export const webdeskPositionSelector = createSelector(
  (state: RootState) => state.ui.appearance.position,
  position => position
);

export const webdeskHorizontalPaddingSelector = createSelector(
  (state: RootState) => state.ui.appearance.horizontalPadding,
  horizontalPadding => horizontalPadding
);

// ******* SITE INTEGRATION SELECTORS *********
export const siteIntegrationSelector = createSelector(
  (state: RootState) => state.ui.siteIntegration,
  si => si
);

export const siteIntegrationTypeSelector = createSelector(
  (state: RootState) =>
    typeof state.ui.siteIntegration !== 'undefined'
      ? state.ui.siteIntegration.type
      : null,
  type => type
);

export const siteIntegrationDebugSelector = createSelector(
  (state: RootState) =>
    typeof state.ui.siteIntegration !== 'undefined'
      ? state.ui.siteIntegration.debug
      : false,
  debug => debug
);

export const siteIntegrationDdcEnglishSelector = createSelector(
  (state: RootState) =>
    typeof state.ui.siteIntegration !== 'undefined' &&
    state.ui.siteIntegration.type === SiteIntegrationType.DEALER_DOT_COM &&
    typeof (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface)
      .chat !== 'undefined'
      ? (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface).chat!
          .buttonText.en_US
      : undefined,
  en => en
);

export const siteIntegrationDdcSpanishSelector = createSelector(
  (state: RootState) =>
    typeof state.ui.siteIntegration !== 'undefined' &&
    state.ui.siteIntegration.type === SiteIntegrationType.DEALER_DOT_COM &&
    typeof (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface)
      .chat !== 'undefined'
      ? (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface).chat!
          .buttonText.es
      : undefined,
  es => es
);

export const siteIntegrationDdcFrenchSelector = createSelector(
  (state: RootState) =>
    typeof state.ui.siteIntegration !== 'undefined' &&
    state.ui.siteIntegration.type === SiteIntegrationType.DEALER_DOT_COM &&
    typeof (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface)
      .chat !== 'undefined'
      ? (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface).chat!
          .buttonText.fr_CA
      : undefined,
  fr => fr
);

export const siteIntegrationDdcCollectVehicleInfoSelector = createSelector(
  (state: RootState) =>
    typeof state.ui.siteIntegration !== 'undefined' &&
    state.ui.siteIntegration.type === SiteIntegrationType.DEALER_DOT_COM &&
    typeof (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface)
      .chat !== 'undefined'
      ? (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface).chat!
          .collectVehicleInfo
      : undefined,
  cvi => cvi
);

export const siteIntegrationDdcHideEpSelector = createSelector(
  (state: RootState) =>
    typeof state.ui.siteIntegration !== 'undefined' &&
    state.ui.siteIntegration.type === SiteIntegrationType.DEALER_DOT_COM &&
    typeof (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface)
      .chat !== 'undefined'
      ? (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface).chat!
          .hideEp
      : undefined,
  hide => hide
);

export const siteIntegrationDdcUseAgentSelector = createSelector(
  (state: RootState) =>
    typeof state.ui.siteIntegration !== 'undefined' &&
    state.ui.siteIntegration.type === SiteIntegrationType.DEALER_DOT_COM &&
    typeof (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface)
      .chat !== 'undefined'
      ? (state.ui.siteIntegration as DealerDotComSiteIntegrationInterface).chat!
          .useAgent
      : undefined,
  ua => ua
);

export const siteIntegrationGenericJavascriptSelector = createSelector(
  (state: RootState) =>
    typeof state.ui.siteIntegration !== 'undefined' &&
    state.ui.siteIntegration.type === SiteIntegrationType.GENERIC
      ? (state.ui.siteIntegration as GenericSiteIntegrationInterface).javascript
      : undefined,
  js => js
);

export const webdeskVerticalPaddingSelector = createSelector(
  (state: RootState) => state.ui.appearance.verticalPadding,
  verticalPadding => verticalPadding
);

export const isCadillacSelector = createSelector(
  (state: RootState) => state.ui.theme.isCadillacTheme,
  isCadillacTheme => isCadillacTheme
);

// ******** FLOATER SELECTORS ********
export const floaterLoadedSelector = createSelector(
  (state: RootState) => state.ui.floater.loaded,
  loaded => loaded
);

export const floaterShownSelector = createSelector(
  (state: RootState) => state.ui.floater.shown,
  shown => shown
);

export const floaterInviteIgnoredCountSelector = createSelector(
  (state: RootState) => state.ui.floater.ignoredCount,
  ignoredCount => ignoredCount
);

export const floaterInviteIgnoredLastSelector = createSelector(
  (state: RootState) => state.ui.floater.ignoredLast,
  ignoredLast => ignoredLast
);

export const floaterInviteClosedLastSelector = createSelector(
  (state: RootState) => state.ui.floater.closedLast,
  closedLast => closedLast
);

export const floaterConfigSelector = createSelector(
  (state: RootState) => state.ui.floater.config,
  config => config
);
